import { makeAutoObservable, runInAction } from "mobx";

import { AttributeBranchType, type OptionItem } from "@/api/admin";
import type { IBranchFull, IProperty } from "@/api/admin/branches";
import { branchesApi } from "@/api/admin/branches/branchApi";
import { isAmenityType } from "@/api/admin/properties";
import type {
  BranchDetailsType,
  BranchHoursFormType,
  BranchMediaFormType,
  BranchReservationType,
  BrandAmenitiesType,
} from "@/components/units";
import { getDefaultPhoneCode } from "@/utils/getDefaultPhoneCode";
import { parseE164 } from "@/utils/parseE164";
import { toE164, toE164FromString } from "@/utils/toE164";

export class DemoBranchStore {
  details: BranchDetailsType;
  businessHours: BranchHoursFormType;
  reservations: BranchReservationType;
  amenities: BrandAmenitiesType;
  media: BranchMediaFormType;
  active: boolean = false;

  // Loading states
  isCreating = false;
  isUpdating = false;
  isFetching = false;

  get isNew(): boolean {
    return !this.details?.id;
  }

  constructor(
    brandId?: string,
    branchId?: string,
    readonly hasFixedBrandId?: boolean
  ) {
    this.details = this.getDefaultDetails(brandId);
    this.businessHours = this.getDefaultBusinessHours();
    this.reservations = this.getDefaultReservations();
    this.amenities = this.getDefaultAmenities();
    this.media = this.getDefaultMedia();
    this.active = false;

    makeAutoObservable(this);
    if (branchId) {
      this.fetchBranch(branchId);
    }
  }

  setDetails(details: BranchDetailsType) {
    this.details = details;
  }

  setBusinessHours(businessHours: BranchHoursFormType) {
    this.businessHours = businessHours;
  }

  setReservations(reservations: BranchReservationType) {
    this.reservations = reservations;
  }

  setAmenities(amenities: BrandAmenitiesType) {
    this.amenities = amenities;
  }

  setMedia(media: BranchMediaFormType) {
    this.media = media;
  }

  setAll(data: {
    details?: BranchDetailsType;
    businessHours?: BranchHoursFormType;
    reservations?: BranchReservationType;
    amenities?: BrandAmenitiesType;
    media?: BranchMediaFormType;
    active?: boolean;
  }) {
    if (data.details) {
      this.details = {
        ...this.details,
        ...data.details,
      };
    }
    if (data.businessHours) {
      this.businessHours = {
        ...this.businessHours,
        ...data.businessHours,
      };
    }
    if (data.reservations) {
      this.reservations = {
        ...this.reservations,
        ...data.reservations,
      };
    }
    if (data.amenities) {
      this.amenities = {
        ...this.amenities,
        ...data.amenities,
      };
    }
    if (data.media) {
      this.media = {
        ...this.media,
        ...data.media,
      };
    }
    if (data.active !== undefined) {
      this.active = data.active;
    }
  }

  getDefaultDetails(brandId?: string): BranchDetailsType {
    const fallbackPhoneCode = getDefaultPhoneCode();

    return {
      nameEn: "",
      nameAr: "",
      phoneCode: fallbackPhoneCode || "",
      phoneNumber: "",
      smsPhoneNumbers: [],
      phone: "",
      descriptionAr: "",
      descriptionEn: "",
      addressEn: "",
      addressAr: "",
      brandId: brandId || "",
      googlePlaceId: "",
      menuUrl: "",
      bookingUrl: "",
      legalEntityId: "",
      reviewSummaryEn: "",
      reviewSummaryAr: "",
      areaId: "",
      streetId: "",
      landmarkId: "",
      cityId: "",
      isTestBranch: false,
    };
  }

  getDefaultBusinessHours(): BranchHoursFormType {
    return {
      schedules: [
        {
          day: "sunday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "monday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "tuesday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "wednesday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "thursday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "friday",
          closed: true,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "saturday",
          closed: true,
          open: "09:00",
          close: "23:00",
        },
      ],
    };
  }

  getDefaultReservations(): BranchReservationType {
    return {
      reservationMethods: [],
      averageWaitTime: "UNDER_10_MIN",
      reservationRequired: false,
      tags: [],
      ambiance: [],
      dressCode: [],
      moment: [],
      priceLevel: [1],
    };
  }

  getDefaultAmenities(): BrandAmenitiesType {
    return {
      amenities: [],
    };
  }

  getDefaultMedia(): BranchMediaFormType {
    return {
      media: [],
      mediaItems: [],
    };
  }

  /**
   * Fetch branch by ID and update store
   */
  async fetchBranch(branchId: string): Promise<IBranchFull> {
    this.isFetching = true;
    try {
      const data = await branchesApi.getBranchById(branchId);
      runInAction(() => {
        const {
          id,
          nameAr,
          nameEn,
          descriptionAr,
          descriptionEn,
          brandId,
          addressEn,
          addressAr,
          phone,
          openingHours,
          reservationRequired,
          reservationMethods,
          averageWaitTime,
          googlePlaceId,
          menuUrl,
          bookingUrl,
          legalEntityId,
          priceLevel,
          mediaItems,
          properties,
          referenceNumber,
          active,
          smsPhoneNumbers,
          reviewSummaryEn,
          reviewSummaryAr,
          area,
          street,
          landmark,
          city,
          cityId,
          isTestBranch,
          brandCountry,
          brandCurrency,
        } = data;

        const parsed = parseE164(phone || "");
        const fallbackPhoneCode = getDefaultPhoneCode();

        this.details = {
          nameAr,
          nameEn,
          descriptionAr: descriptionAr || "",
          descriptionEn: descriptionEn || "",
          brandId: (brandId as string) || "",
          addressEn: addressEn || "",
          addressAr: addressAr || "",
          phoneCode: (parsed?.phoneCode ?? fallbackPhoneCode) || "",
          phoneNumber: parsed?.phoneNumber ?? "",
          smsPhoneNumbers,
          phone: "",
          googlePlaceId,
          menuUrl,
          bookingUrl,
          legalEntityId,
          id,
          referenceNumber: referenceNumber || "",
          reviewSummaryEn: reviewSummaryEn || "",
          reviewSummaryAr: reviewSummaryAr || "",
          areaId: area?.id || "",
          streetId: street?.id || "",
          landmarkId: landmark?.id || "",
          cityId: cityId || city?.id || "",
          isTestBranch: isTestBranch || false,
          brandCountry: brandCountry || "",
          brandCurrency: brandCurrency || "",
        };

        this.businessHours = {
          schedules: openingHours.schedules.map(schedule => {
            if (!schedule.closed) {
              return {
                ...schedule,
                open: this.shiftTime(schedule.open, 3),
                close: this.shiftTime(schedule.close, 3),
              };
            }

            const defaultSchedule =
              this.getDefaultBusinessHours().schedules.find(
                d => d.day === schedule.day
              );

            return {
              ...schedule,
              open: defaultSchedule?.open ?? schedule.open,
              close: defaultSchedule?.close ?? schedule.close,
            };
          }),
        };

        const amenitiesList =
          properties?.filter(
            item =>
              isAmenityType(item.type) ||
              item.type === AttributeBranchType.CUISINE
          ) ?? [];

        const tagsList =
          properties?.filter(item => item.type === AttributeBranchType.TAG) ??
          [];

        const ambiancesList =
          properties?.filter(
            item => item.type === AttributeBranchType.AMBIANCE
          ) ?? [];

        const dressCodeList =
          properties?.filter(
            item => item.type === AttributeBranchType.DRESS_CODE
          ) ?? [];

        const momentsList =
          properties?.filter(
            item => item.type === AttributeBranchType.MOMENT
          ) ?? [];

        this.amenities = {
          amenities: amenitiesList.map(item => item.id),
        };

        const tagsIdsList = tagsList.map(item => item.id);
        const ambiancesIdsList = ambiancesList.map(item => item.id);
        const dressCodeIdsList = dressCodeList.map(item => item.id);
        const momentsIdsList = momentsList.map(item => item.id);

        this.reservations = {
          reservationRequired,
          reservationMethods,
          averageWaitTime,
          priceLevel: priceLevel ? [priceLevel] : [],
          tags: tagsIdsList,
          ambiance: ambiancesIdsList,
          dressCode: dressCodeIdsList,
          moment: momentsIdsList,
        };

        this.media = {
          media: mediaItems
            ? [...mediaItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            : [],
          mediaItems: mediaItems
            ? [...mediaItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            : [],
        };

        this.isFetching = false;
        this.active = !!active;
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.isFetching = false;
      });
      throw error;
    }
  }

  /**
   * Prepare data for API call
   */
  prepareData() {
    const formattedNumber = toE164(
      this.details?.phoneCode ?? "",
      this.details?.phoneNumber ?? ""
    );

    const price = Array.isArray(this.reservations?.priceLevel)
      ? this.reservations.priceLevel[0]
      : typeof this.reservations?.priceLevel === "number"
        ? this.reservations.priceLevel
        : undefined;

    const propertyPayload: IProperty[] | string[] = [
      ...(this.amenities?.amenities ?? []),
      ...(this.reservations?.tags ?? []),
      ...(this.reservations?.ambiance ?? []),
      ...(this.reservations?.dressCode ?? []),
      ...(this.reservations?.moment ?? []),
    ];

    const smsPhonePayload = Array.isArray(this.details.smsPhoneNumbers)
      ? this.details.smsPhoneNumbers
          .map(phone => toE164FromString(phone))
          .filter((v): v is string => Boolean(v))
      : undefined;

    const payload: IBranchFull = {
      ...(this.details?.referenceNumber
        ? { referenceNumber: this.details.referenceNumber }
        : {}),
      nameAr: this.details?.nameAr?.trim() || "",
      nameEn: this.details?.nameEn?.trim() || "",
      descriptionAr: this.details?.descriptionAr?.trim(),
      descriptionEn: this.details?.descriptionEn?.trim(),
      legalEntityId: this.details?.legalEntityId,
      menuUrl: this.details?.menuUrl,
      bookingUrl: this.details?.bookingUrl,
      googlePlaceId: this.details?.googlePlaceId,
      phone: formattedNumber,
      smsPhoneNumbers: smsPhonePayload,
      addressEn: this.details?.addressEn || "",
      addressAr: this.details?.addressAr || "",
      brandId: this.details?.brandId,
      longitude: this.details?.longitude,
      latitude: this.details?.latitude,
      openingHours: {
        schedules:
          this.businessHours?.schedules.map(schedule => ({
            ...schedule,
            open: this.shiftTime(schedule.open, -3),
            close: this.shiftTime(schedule.close, -3),
          })) || [],
      },
      reservationRequired: this.reservations?.reservationRequired || false,
      reservationMethods: [...(this.reservations?.reservationMethods || [])],
      priceLevel: price,
      averageWaitTime: this.reservations?.averageWaitTime || "UNDER_10_MIN",
      active: this.active,
      mediaItems: this.media?.mediaItems ?? [],
      properties: propertyPayload,
      googleTotalReviews: this.details.googleTotalReviews,
      googlePlaceAverageRating: this.details.googlePlaceAverageRating,
      reviewSummaryEn: this.details?.reviewSummaryEn?.trim(),
      reviewSummaryAr: this.details?.reviewSummaryAr?.trim(),
      areaId: this.details.areaId || null,
      streetId: this.details.streetId || null,
      landmarkId: this.details.landmarkId || null,
      cityId: this.details.cityId || null,
    };

    return payload;
  }

  parseTime(val: string) {
    const [hours, minutes] = val.split(":").map(Number);
    return new Date(Date.UTC(1970, 0, 1, hours, minutes));
  }

  shiftTime(val: string, offsetHours: number) {
    const date = this.parseTime(val);
    const shiftedHours = (date.getUTCHours() + offsetHours + 24) % 24;
    const minutes = date.getUTCMinutes();
    return `${String(shiftedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  async upsertBranch(): Promise<IBranchFull> {
    if (!this.details?.id) {
      return this.createBranch();
    }
    return this.updateBranch();
  }

  /**
   * Create branch and update store
   */
  async createBranch(skipRefetch = false): Promise<IBranchFull> {
    this.isCreating = true;

    try {
      const payload = this.prepareData();
      const created = await branchesApi.postNewBranch(payload);
      runInAction(() => {
        this.isCreating = false;
      });

      if (skipRefetch) {
        return created;
      }

      // Fetch the created branch to get full data
      return await this.fetchBranch(created.id);
    } catch (error) {
      runInAction(() => {
        this.isCreating = false;
      });
      throw error;
    }
  }

  /**
   * Update branch and refresh data
   */
  async updateBranch(): Promise<IBranchFull> {
    if (!this.details?.id) {
      throw new Error("Cannot update branch: no branch ID");
    }

    this.isUpdating = true;

    try {
      const payload = this.prepareData();
      await branchesApi.patchBranch(this.details.id, payload);
      // Refresh branch data
      const refreshed = await this.fetchBranch(this.details.id);
      runInAction(() => {
        this.isUpdating = false;
      });
      return refreshed;
    } catch (error) {
      runInAction(() => {
        this.isUpdating = false;
      });
      throw error;
    }
  }

  updateBranchCoreFields(payload: {
    nameEn: string;
    nameAr: string;
    descriptionEn: string;
    descriptionAr: string;
    phone: string;
    addressEn: string;
    addressAr: string;
    openingHours: BranchHoursFormType;
    amenities: OptionItem[];
    priceLevel?: number;
    reviewSummaryEn?: string;
    reviewSummaryAr?: string;
  }) {
    runInAction(() => {
      const parsed = parseE164(payload.phone || "");
      const fallbackPhoneCode = getDefaultPhoneCode();

      this.details = {
        ...this.details,
        nameEn: payload.nameEn,
        nameAr: payload.nameAr,
        descriptionEn: payload.descriptionEn,
        descriptionAr: payload.descriptionAr,
        phone: payload.phone,
        phoneCode: parsed?.phoneCode ?? fallbackPhoneCode,
        phoneNumber: parsed?.phoneNumber ?? "",
        addressEn: payload.addressEn,
        addressAr: payload.addressAr,
        reviewSummaryEn: payload.reviewSummaryEn ?? "",
        reviewSummaryAr: payload.reviewSummaryAr ?? "",
      };

      const normalizedSchedules = payload.openingHours.schedules.map(
        schedule => {
          if (!schedule.closed) {
            return {
              ...schedule,
              open: this.shiftTime(schedule.open, 3),
              close: this.shiftTime(schedule.close, 3),
            };
          }

          const defaultSchedule = this.getDefaultBusinessHours().schedules.find(
            d => d.day === schedule.day
          );

          return {
            ...schedule,
            open: defaultSchedule?.open ?? schedule.open,
            close: defaultSchedule?.close ?? schedule.close,
          };
        }
      );

      this.businessHours = {
        schedules: normalizedSchedules,
      };

      this.amenities = {
        amenities: payload.amenities.map(item => item.id),
      };

      const normalizedPriceLevel =
        payload.priceLevel ?? this.getDefaultReservations().priceLevel[0];

      this.reservations = {
        ...this.reservations,
        priceLevel: [normalizedPriceLevel],
      };
    });
  }

  getSnapshot(): { branchId?: string; brandId?: string; groupId?: string } {
    return {
      branchId: this.details?.id,
      brandId: this.details?.brandId,
    };
  }
}
